import type { AuthMethodLookupResponse, KeystoneSession } from '@rtaoutdoorlivingllc/keystone-react';
import { useMemo } from 'react';

const KEYSTONE_TOKEN_KEY = 'ks_token';

/**
 * Opens the client-side auth gate without an interactive Keystone sign-in so the dev-bridge and the
 * provider tree mount in a local dev tab. Requires a dev build AND an explicit opt-in: vite replaces
 * `import.meta.env.DEV` with the literal `false` in a production build, so this const folds to `false`
 * and every branch guarded by it is dropped by the minifier.
 */
export const devAuthBypassEnabled: boolean = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

if (import.meta.env.DEV && devAuthBypassEnabled) {
	console.warn(
		'%c⚠ DEV AUTH BYPASS ACTIVE (VITE_DEV_AUTH_BYPASS=true) — the client auth gate is open with no Keystone sign-in. No token is minted or forged; server auth is untouched, so unauthenticated API calls will legitimately 401. Never enable this outside local development.',
		'background:#b91c1c;color:#fff;font-weight:bold;padding:4px 8px;font-size:13px;'
	);
}

export type DevSession = {
	/** True only while the bypass is enabled. AuthGate opens on this alone. */
	active: boolean;
	/** Keystone-shaped stand-in, or null when the bypass is off. */
	session: KeystoneSession | null;
};

// Seeding localStorage lets the real KeystoneProvider adopt an operator-supplied token during its own
// bootstrap, so authenticated endpoints work through the normal session rather than a parallel path.
const resolveDevToken = (): string => {
	// Re-guarded on DEV so the VITE_DEV_KS_TOKEN literal is statement-level unreachable, never inlined into a production bundle.
	if (!import.meta.env.DEV) return '';

	const configured: string = import.meta.env.VITE_DEV_KS_TOKEN ?? '';

	if (configured) window.localStorage.setItem(KEYSTONE_TOKEN_KEY, configured);

	return configured || window.localStorage.getItem(KEYSTONE_TOKEN_KEY) || '';
};

const DEFAULT_DEV_ROLES = ['blueprint:super_admin'];

// RequireRole reads appRoles off the Keystone user, so a null user closes every guarded route and
// unmounts the subtree that <DevBridge /> lives in. Roles here are client-side only: the API still
// validates a real JWT, so a bypassed tab with no token gets legitimate 401s.
const resolveDevRoles = (): Array<string> => {
	if (!import.meta.env.DEV) return [];

	const configured = import.meta.env.VITE_DEV_AUTH_ROLES;

	return typeof configured === 'string' && configured.trim() !== ''
		? configured.split(',').map((role) => role.trim()).filter(Boolean)
		: DEFAULT_DEV_ROLES;
};

export const useDevSession = (): DevSession => {
	return useMemo<DevSession>(() => {
		if (!import.meta.env.DEV || !devAuthBypassEnabled) return { active: false, session: null };

		const token = resolveDevToken();
		const appRoles = resolveDevRoles();

		return {
			active: true,
			session: {
				status: 'ready',
				user: { email: 'dev-bypass@localhost', appRoles } as never,
				error: null,
				getToken: async (): Promise<string> => token,
				lookupOrgMethod: async (): Promise<AuthMethodLookupResponse> => ({ method: null, methods: [], org: null, defaultAuthDomain: null }),
				runtimeConfig: null,
				signIn: (): void => {},
				signInWithPassword: async (): Promise<void> => {},
				signOut: async (): Promise<void> => {}
			}
		};
	}, []);
};
